import {
  Banner,
  Box,
  Divider,
  DropdownButtonContext,
} from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import { memo, useCallback, useContext, useEffect, useMemo } from 'react';

import { aiApi } from 'apis/ai';
import { AiLoadingState } from 'components/aiLoadingState';
import { CopilotDropdownActionContext } from 'components/copilotDropdown';
import { CopilotDropdownFooter } from 'components/copilotDropdown/components';
import { HtmlRenderer } from 'components/htmlRenderer';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { sanitizationProfile, useEditorContext } from 'utils/useEditorContext';

import { CopilotDropdownHeader } from '../../../copilotDropdown/components/header';
import { EditorCopilotDropdownContentBox } from '../contentBox';

const additionalFeedback = {
  feature: 'spelling_and_grammar',
};

/**
 * Represents the component that checks spelling and grammar of selected text or
 * entire content of the editor.
 */
export const EditorCopilotDropdownSpellingAndGrammar = memo(
  function EditorCopilotDropdownSpellingAndGrammar(): JSX.Element {
    const { onBack, actionsComputed } = useContext(
      CopilotDropdownActionContext,
    );
    const csid = useAppSelector(
      (state) => state.conversation.metaConversation.conversation.id,
    );
    const actionComputed = actionsComputed.checkSpellingAndGrammar;
    const { insertBelow, isEmpty, replace, getSelectedText } =
      useEditorContext();
    const dropdownContext = useContext(DropdownButtonContext);
    const [checkSpellingAndGrammar, { data }] =
      aiApi.useLazyReadStreamAiCheckGrammarAndSpellingQuery();

    const feedbackId = data?.feedback_id ?? null;

    const correctedText = useMemo(() => {
      return data?.text ?? undefined;
    }, [data?.text]);

    const canReplaceOrInsert = !!correctedText && !isEmpty;

    const content = useMemo(() => {
      if (data?.meta?.isLoading) {
        return <AiLoadingState label="" />;
      }

      if (data?.meta?.isError) {
        return (
          <Banner
            variant="fail"
            description={
              <span>
                An error occurred while checking grammar and spelling.
              </span>
            }
          />
        );
      }

      if (isEmpty && !data?.meta?.isStreaming) {
        return (
          <Banner
            variant="warning"
            description={
              <span>There is no text to check grammar and spelling of.</span>
            }
          />
        );
      }

      if (correctedText) {
        return (
          <HtmlRenderer
            sanitizationProfileType={sanitizationProfile}
            html={correctedText}
          />
        );
      }
    }, [data, isEmpty, correctedText]);

    const handleInsertBelow = useCallback(() => {
      if (!correctedText) {
        return;
      }

      insertBelow(correctedText);

      dropdownContext.closeDropdown();

      ampli.agentCopilotTextInsertBelowClicked({
        key: 'checkSpellingAndGrammar',
      });
    }, [correctedText, dropdownContext, insertBelow]);

    const handleReplace = useCallback(() => {
      if (!correctedText) {
        return;
      }

      replace(correctedText);

      dropdownContext.closeDropdown();

      ampli.agentCopilotTextReplaceClicked({
        key: 'checkSpellingAndGrammar',
      });
    }, [correctedText, dropdownContext, replace]);

    useEffect(() => {
      if (dropdownContext.dropdownOpen && !isEmpty) {
        const text = getSelectedText();
        checkSpellingAndGrammar({
          text,
          ...(csid ? { csid: csid.toString() } : {}),
        });
      }
    }, [
      checkSpellingAndGrammar,
      dropdownContext.dropdownOpen,
      getSelectedText,
      isEmpty,
    ]);

    return (
      <Box style={{ maxWidth: 768, minWidth: 540 }}>
        <CopilotDropdownHeader
          onBack={onBack}
          title={actionComputed.title}
          feedbackId={feedbackId}
          additionalFeedback={additionalFeedback}
        />

        <EditorCopilotDropdownContentBox isEmphasized={!!correctedText}>
          {content}
        </EditorCopilotDropdownContentBox>

        <Divider />

        <CopilotDropdownFooter
          handleInsertBelow={handleInsertBelow}
          disableInsertBelow={!canReplaceOrInsert}
          handleReplace={handleReplace}
          disableReplace={!canReplaceOrInsert}
          disableImproveReply={!canReplaceOrInsert}
          draftContent={correctedText}
        />
      </Box>
    );
  },
);
